import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Logo, MonoLabel } from '../components/primitives/index.js';

// 4-phase animation while the analysis call is in flight. Matches the prototype
// (SCREENS.md #08): visual progression so the ~5-8s wait feels intentional.
const PHASES = [
  'Citim datele extrase din PV…',
  'Căutăm articole de lege relevante…',
  'Verificăm spețe similare câștigate…',
  'Construim strategia de apărare…',
];

export function ContestAnalyzing({ id }: { id: string }) {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();
  const [phase, setPhase] = useState(0);
  const fired = useRef(false);

  const analyze = useMutation({
    mutationFn: () => api.analyzeContest(id),
    onSuccess: () => setLocation(`/contest/${id}/result`),
  });

  useEffect(() => {
    if (!me.data || fired.current) return;
    fired.current = true;
    analyze.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.data]);

  // Cycle phases every ~1.8s, capped to last phase while we wait.
  useEffect(() => {
    if (analyze.isSuccess || analyze.isError) return;
    const t = setInterval(() => {
      setPhase((p) => Math.min(p + 1, PHASES.length - 1));
    }, 1800);
    return () => clearInterval(t);
  }, [analyze.isSuccess, analyze.isError]);

  if (analyze.isError) {
    return (
      <Shell>
        <div className="text-center max-w-md mx-auto py-16">
          <div className="text-4xl mb-4">⚠️</div>
          <h1 className="text-2xl font-medium mb-2">Analiza a eșuat</h1>
          <p className="text-muted leading-relaxed mb-6">
            Ceva n-a mers la analiza juridică. Reîncearcă sau revino la pasul de întrebări dacă
            vrei să modifici răspunsurile.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="ghost"
              pill
              icon={false}
              onClick={() => setLocation(`/contest/${id}/questions`)}
            >
              ← Modifică răspunsuri
            </Button>
            <Button
              pill
              icon="↻"
              onClick={() => {
                fired.current = false;
                analyze.mutate();
              }}
            >
              Reîncearcă
            </Button>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-xl mx-auto flex flex-col items-center text-center py-10 md:py-16">
        <MonoLabel>AI Lucrează</MonoLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-display-4 font-medium tracking-tight mt-3 mb-8 md:mb-10 text-pretty">
          Verificăm 47 de criterii de nulitate și spețe similare.
        </h1>

        <CircularProgress />

        <ul className="mt-12 space-y-3 w-full max-w-md text-left">
          {PHASES.map((label, i) => {
            const done = i < phase;
            const active = i === phase;
            return (
              <li key={i} className="flex items-center gap-3 text-[15px]">
                <span
                  className={[
                    'h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition',
                    done
                      ? 'bg-accent text-fg'
                      : active
                        ? 'bg-fg text-bg animate-pulse'
                        : 'bg-bg-2 text-faint',
                  ].join(' ')}
                >
                  {done ? '✓' : i + 1}
                </span>
                <span className={done ? 'text-muted line-through' : active ? 'text-fg' : 'text-faint'}>
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </Shell>
  );
}

// Simple SVG ring with rotating gradient. Phase 4 can swap to an animated
// stroke-dashoffset approach if we want true % progress.
function CircularProgress() {
  return (
    <div className="relative w-32 h-32">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="44" fill="none" stroke="#E8E6DF" strokeWidth="4" />
        <circle
          cx="50"
          cy="50"
          r="44"
          fill="none"
          stroke="oklch(0.72 0.18 145)"
          strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray="276"
          strokeDashoffset="138"
          style={{
            animation: 'spin 1.4s linear infinite',
            transformOrigin: '50% 50%',
            transformBox: 'fill-box',
          }}
        />
      </svg>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg flex flex-col">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="flex-1 px-4 md:px-6 py-8 md:py-12">{children}</main>
    </div>
  );
}
